<script setup lang="ts">
  import '@/shared/assets/scss/flow.scss'

  import { useFlow } from '@/shared/composables/useFlow/useFlow'
  import { useFlowSteps } from '@/shared/composables/useFlowSteps/useFlowSteps'
  import { useFeatureToggle } from '@/shared/composables/useFeatureToggle'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'
  import { is504, isErrorWithRequiredEmptyParams } from '@/shared/utils'
  import { incorrectTravelTypeResponse } from '@/shared/constants/validationMessages.constants'
  import { AxiosError } from 'axios'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { STATE_CACHE_KEY, useCoreStore, useVisitorsStore } from '@/shared/store'
  import { useCacheService } from '@/shared/composables/useCacheService'
  import { QATAR_INBOUND_KEY } from '@/shared/constants/other.constants'
  import { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { useHandleRedirect } from '@/shared/composables/useHandleRedirect'

  import { FlowWidget } from '@/widgets'
  import { BaseError } from '@/shared/components/rebranding/BaseError'
  import { FlowCardSkeleton } from '@/shared/components/rebranding/FlowCard'

  const routeByValidationError = useHandleRedirect

  const visitorsStore = useVisitorsStore()
  const localStorageCacheService = useCacheService()
  const sessionStorageCacheService = useCacheService('session')
  const coreStore = useCoreStore()
  const {
    isReady: isFeatureToggleReady,
    isLoading: isFeatureToggleLoading,
    initFeatureToggle,
  } = useFeatureToggle()

  const { flowScheme } = useFlow()
  const { currentStep, stepsCount, step } = useFlowSteps(flowScheme)
  const { push, replace, currentRoute } = useRouter()

  visitorsStore.$subscribe((_, state) => {
    const { holder, visitors, ...visitInfo } = state
    localStorageCacheService.set(STATE_CACHE_KEY, visitInfo)
    sessionStorageCacheService.set(STATE_CACHE_KEY, { holder, visitors })
    localStorage.setItem(QATAR_INBOUND_KEY, state.holder.country || '')
  })

  const error = ref('')
  const loading = ref(false)
  const homeUrl = useHomeUrl()
  const error504 = ref(false)

  const isLoadingActive = computed(() => loading.value || isFeatureToggleLoading.value)

  const isStopBack = computed(() => coreStore.isStopBack)
  const backCallback = computed(() => coreStore.backCallback)
  const disableProgress = computed(
    () => currentRoute.value.name === PurchaseFlowRoutes.QUOTE || !!error.value
  )

  const nextStep = async (page: AvailableFlowRoutes) => {
    await push({ name: page || step.value.next })
  }

  const resolveError = () => {
    error.value = ''
    error504.value = false
  }

  const handleOnBack = () => {
    if (isStopBack.value) {
      backCallback.value?.()
      return
    }

    resolveError()

    if (step.value.current === PurchaseFlowRoutes.TRAVEL_DATE) {
      window.location.href = homeUrl
      return
    }

    push({ name: step.value.prev })
  }

  const handleError = (errorMessage: unknown) => {
    error.value = errorMessage instanceof Error ? errorMessage.message : String(errorMessage)
    console.error(error.value)
  }

  const requestPool = new Set()

  const onLoading = async (
    request: () => Promise<any>,
    requestId = 'request',
    withLoader = true,
    withError = true,
    infinityLoader = false
  ) => {
    const routeFrom = currentRoute.value.fullPath
    requestPool.add(requestId)
    if (withLoader) loading.value = true
    try {
      await request()
    } catch (error) {
      if (error === incorrectTravelTypeResponse) {
        requestPool.delete(requestId)
        return push({ name: PurchaseFlowRoutes.TRAVEL_DATE })
      }
      if (isErrorWithRequiredEmptyParams(error)) {
        requestPool.delete(requestId)
        return push(routeByValidationError(error.response?.data.requiredEmptyParams))
      }
      if (is504(error as AxiosError)) {
        error504.value = true
        handleError(error)
      }
      if (routeFrom === currentRoute.value.fullPath && withError) {
        handleError(error)
      }
    }
    if (infinityLoader) return
    requestPool.delete(requestId)
    if (requestPool.size > 0) return
    loading.value = false
  }

  watch(
    currentRoute,
    () => {
      resolveError()
      const stepIndex = flowScheme.findIndex((stepName) => stepName === currentRoute.value.name)
      if (stepIndex >= 0) {
        currentStep.value = stepIndex + 1
      } else {
        replace({ name: flowScheme[0] })
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    initFeatureToggle()
  })
</script>

<template>
  <FlowWidget
    :disable-progress-bar="disableProgress"
    :steps-count="stepsCount"
    :current-step="currentStep"
    @go-back="handleOnBack"
  >
    <BaseError v-if="error" :message="error" :error504="error504" @reload="resolveError" />
    <template v-else>
      <div class="flow-view">
        <FlowCardSkeleton v-if="isLoadingActive" />

        <RouterView
          v-if="isFeatureToggleReady"
          v-show="!isLoadingActive"
          @proceed="nextStep"
          @load="onLoading"
          @back="handleOnBack"
          @error="handleError"
        />
      </div>
    </template>
  </FlowWidget>
</template>

<style lang="scss" scoped>
  .flow-view {
    position: relative;
    width: 100%;
  }
</style>
